import { BusinessCardScanner, Events, MyCardHolder, PaymentGateway } from '@ice-web-app/assets';
import { DsButton, DsConfirmModal, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import { useTranslation } from '@ice-web-app/shared-hooks';
import { Box, Divider, Menu, Stack } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Dispatch, FC, ReactElement, SetStateAction, useState } from 'react';
import { toast } from 'react-toastify';
import { MiddleMenuSvg } from '../MiddleMenuSvg';
import AddQuickLinkModal from './AddQuickLinkModal';
import QuickLink from './QuickLink';

interface Props {
  anchorEl: HTMLElement | null;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
}

const MiniNavbarLinkMenu: FC<Props> = ({ anchorEl, setAnchorEl }) => {
  const { t } = useTranslation();
  const open = Boolean(anchorEl);
  const [offset, setOffset] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [openAddLink, setOpenAddLink] = useState(false);
  const [openDeleteLink, setOpenDeleteLink] = useState<{
    open: boolean;
    activeLink: { key: string; icon: ReactElement; text: string } | null;
  }>({ open: false, activeLink: null });
  const [links, setLinks] = useState([
    { key: 'BUSINESS_CARD_SCANNER', icon: <BusinessCardScanner />, text: t('BUSINESS_CARD_SCANNER') },
    { key: 'MY_CARD_HOLDER', icon: <MyCardHolder />, text: t('MY_CARD_HOLDER') },
    { key: 'PAYMENTS', icon: <PaymentGateway />, text: t('PAYMENTS') },
    { key: 'EVENTS', icon: <Events />, text: t('EVENTS') },
  ]);

  return (
    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      open={open}
      onClose={() => {
        setAnchorEl(null);
        setEditMode(false);
      }}
      transformOrigin={{ horizontal: 'left', vertical: 'center' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'center' }}
      slotProps={{
        paper: {
          id: 'MiniNavbarLinkMenu-Paper',
          ref: (el) => {
            setTimeout(() => {
              if (el) {
                setOffset(
                  Number(getComputedStyle(el)?.transformOrigin.split('px')[1].trim()) -
                    355 -
                    (Math.ceil(links.length / 3) - 2) * 60,
                );
              }
            }, 10);
          },
          sx: {
            overflow: 'visible',
            border: '.6px solid',
            boxShadow: 'none !important',
            marginLeft: 5,
            borderRadius: 2,
            borderColor: ({ palette }) => palette.text.secondary,
            '& ul': {
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          top: `${offset * 2}px`,
          bottom: 0,
          left: -22.2,
          marginTop: 'auto',
          marginBottom: 'auto',
          height: 70,
        }}
      >
        {<MiddleMenuSvg />}
      </Box>

      <Stack sx={{ height: 40, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}>
        <DsTypography variant="body2_500">{t('QUICK_LINKS')}</DsTypography>
        <DsTypography
          onClick={() => setEditMode(!editMode)}
          variant="body2"
          color="primary.main"
          sx={{ cursor: 'pointer', position: 'absolute', right: 16 }}
        >
          {t(editMode ? 'CANCEL' : 'CUSTOMIZE')}
        </DsTypography>
      </Stack>
      <Divider />
      <Box sx={{ p: 4 }}>
        <Grid container mb={4} rowGap={4} direction="row" width={300}>
          {links.map((link, index) => (
            <Grid key={index} xs={4}>
              <QuickLink
                icon={link.icon}
                text={link.text}
                width={100}
                isEditMode={editMode && link.key !== 'BUSINESS_CARD_SCANNER'}
                onClickRemoveBtn={() => setOpenDeleteLink({ open: true, activeLink: link })}
              />
            </Grid>
          ))}
        </Grid>
        <DsButton
          size="small"
          variant="contained"
          fullWidth
          startIcon={<DsIcon icon="PLUS" />}
          onClick={() => setOpenAddLink(true)}
        >
          {t('ADD_FROM_LIST')}
        </DsButton>
      </Box>

      <DsConfirmModal
        open={openDeleteLink.open}
        type="warning"
        title={t('CONFIRM_DELETE_LABEL_QUICK_LINK', { label: openDeleteLink.activeLink?.text })}
        onApprove={() => {
          setLinks((preState) => {
            const newState = preState.filter((state) => state.key !== openDeleteLink.activeLink?.key);
            return newState;
          });
          setOpenDeleteLink({ open: false, activeLink: null });
          setAnchorEl(null);
          setEditMode(false);
          toast.success(t('CHANGES_SAVED_SUCCESSFULLY'));
        }}
        onReject={() => setOpenDeleteLink({ open: false, activeLink: null })}
      />

      <AddQuickLinkModal open={openAddLink} setOpen={setOpenAddLink} quickLink={links} setQuickLink={setLinks} />
    </Menu>
  );
};
export default MiniNavbarLinkMenu;
